import { useEffect, useRef, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { useLocation } from "react-router-dom";
import { ApiError } from "@/api/api-axios";
interface GoogleCredentialResponse {
  clientId: string;
  credential: string; // ⭐ chính là ID_TOKEN
  select_by: string;
}

interface GoogleLoginButtonProps {
  rememberMe: boolean;
  setIsGoogleLoading: (loading: boolean) => void;
  setError: (error: string) => void;
  disabled?: boolean;
  callbackUrl?: string;
}

export default function GoogleLoginButton({
  rememberMe,
  setIsGoogleLoading,
  setError,
  disabled = false,
  callbackUrl,
}: GoogleLoginButtonProps) {
  const { loginWithGoogle } = useAuth();
  const location = useLocation();
  const buttonRef = useRef<HTMLDivElement>(null);

  const handleCallback = useCallback(
    async (response: GoogleCredentialResponse) => {
      setIsGoogleLoading(true);
      setError("");

      try {
        const idToken = response.credential; // ⭐ ID_TOKEN (JWT)
        const result = await loginWithGoogle(idToken, rememberMe);

        if (result.success) {
          // Ưu tiên callbackUrl từ props, sau đó location.state, cuối cùng là mặc định
          const redirectUrl = callbackUrl || location.state?.from?.pathname || "/dashboard";
          // Sử dụng setTimeout để đợi state được cập nhật trước khi navigate
          setTimeout(() => {
            window.location.href = redirectUrl;
          }, 100);
        } else {
          setError(result.message);
        }
      } catch (error: unknown) {
        const message =
          ((error as ApiError).data?.message as string) ||
          "Đăng nhập Google thất bại!";
        setError(message);
      } finally {
        setIsGoogleLoading(false);
      }
    },
    [
      loginWithGoogle,
      rememberMe,
      location,
      callbackUrl,
      setIsGoogleLoading,
      setError,
    ]
  );

  useEffect(() => {
    // Load Google script nếu chưa có
    const loadGoogleScript = () => {
      return new Promise<void>((resolve) => {
        if (window.google?.accounts?.id) {
          resolve();
          return;
        }

        const script = document.createElement("script");
        script.src = "https://accounts.google.com/gsi/client";
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => {
          console.error("Failed to load Google Sign-In script");
          resolve();
        };
        document.head.appendChild(script);
      });
    };

    const initializeGoogle = async () => {
      await loadGoogleScript();

      if (!window.google?.accounts?.id) {
        console.error("Google Sign-In script not loaded");
        return;
      }

      if (!buttonRef.current) return;

      const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
      if (!clientId) {
        console.error("VITE_GOOGLE_CLIENT_ID is not set");
        return;
      }

      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: handleCallback,
      });

      window.google.accounts.id.renderButton(buttonRef.current, {
        type: "standard",
        theme: "outline",
        size: "large",
        text: "signin_with",
        width: "100%",
      });
    };

    initializeGoogle();
  }, [handleCallback]);

  return <div ref={buttonRef} className="w-full overflow-hidden btn-google" />;
}
